import { useEffect, useState } from 'react';

import {
  DEFAULT_COST_BASED_PAGES_PER_CREDIT,
  LEGACY_PAGES_PER_CREDIT,
} from '../../../../../../../backend/src/billing/billing-constants';
import { CreditCalculationModel } from '../../../../../../../backend/src/billing/billing-types';
import { useUpdateCreditCalculation } from '@/api/mutations/useUpdateCreditCalculation';
import type { Organisation } from '@/api/types/backend-dto-types';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import {
  SettingsRow,
  SettingsSaveButton,
  SettingsSaveStatus,
  SettingsSection,
  SettingsSectionFooter,
} from '@/new/components/settings-section';
import { Input } from '@/new/components/shadcn/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';

export function CreditCalculationSettings({ organisation }: { organisation: Organisation }) {
  const [model, setModel] = useState(organisation.creditCalculationModel ?? CreditCalculationModel.Legacy);
  const [pages, setPages] = useState(String(organisation.pagesPerCredit ?? LEGACY_PAGES_PER_CREDIT));
  const [confirming, setConfirming] = useState(false);
  const { mutate, isLoading } = useUpdateCreditCalculation(organisation.id);
  const hasPlan = organisation.creditCalculationModel !== null;
  const pagesPerCredit = Number(pages);
  const changed = model !== organisation.creditCalculationModel || pagesPerCredit !== organisation.pagesPerCredit;
  const valid = Number.isInteger(pagesPerCredit) && pagesPerCredit > 0 && pagesPerCredit <= 2147483647;

  useEffect(() => {
    setModel(organisation.creditCalculationModel ?? CreditCalculationModel.Legacy);
    setPages(String(organisation.pagesPerCredit ?? LEGACY_PAGES_PER_CREDIT));
    setConfirming(false);
  }, [organisation.id, organisation.creditCalculationModel, organisation.pagesPerCredit]);

  function changeModel(value: CreditCalculationModel) {
    setModel(value);
    setPages(
      String(
        value === CreditCalculationModel.Legacy
          ? LEGACY_PAGES_PER_CREDIT
          : organisation.creditCalculationModel === value
            ? (organisation.pagesPerCredit ?? DEFAULT_COST_BASED_PAGES_PER_CREDIT)
            : DEFAULT_COST_BASED_PAGES_PER_CREDIT,
      ),
    );
  }

  return (
    <SettingsSection
      title="Credit calculation"
      description="Choose how usage is converted into credits. Changes create a new plan effective immediately, preserving the previous settings in plan history."
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (hasPlan && changed && valid) setConfirming(true);
        }}
      >
        <SettingsRow
          title="Credit calculation model"
          description="Legacy pricing is 1 page per credit, 10 credits per thread. Cost-based is variable pages per credit, with CBP for Kim invocations. Separate from usage-based or seat-based pricing."
        >
          <Select value={model} onValueChange={changeModel} disabled={!hasPlan || isLoading}>
            <SelectTrigger aria-label="Credit calculation model" className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={CreditCalculationModel.Legacy}>Legacy</SelectItem>
              <SelectItem value={CreditCalculationModel.CostBased}>Cost-based</SelectItem>
            </SelectContent>
          </Select>
        </SettingsRow>
        <SettingsRow
          title="Pages per credit"
          description={
            model === CreditCalculationModel.Legacy
              ? 'Legacy plans use 1 page per credit.'
              : `Cost-based plans default to ${DEFAULT_COST_BASED_PAGES_PER_CREDIT} pages per credit.`
          }
        >
          <Input
            aria-label="Pages per credit"
            className="w-48"
            type="number"
            min={1}
            max={2147483647}
            step={1}
            value={pages}
            onChange={(event) => setPages(event.target.value)}
            disabled={!hasPlan || isLoading || model === CreditCalculationModel.Legacy}
          />
        </SettingsRow>
        <SettingsSectionFooter>
          <SettingsSaveStatus variant={!hasPlan ? 'neutral' : changed ? 'unsaved' : 'saved'}>
            {!hasPlan
              ? 'Configure a billing plan before changing credit calculation.'
              : changed
                ? 'You have unsaved changes'
                : 'All changes saved'}
          </SettingsSaveStatus>
          <SettingsSaveButton
            type="submit"
            label="Review change before applying"
            hasUnsavedChanges={hasPlan && changed}
            isLoading={isLoading}
            disabled={!hasPlan || !changed || !valid}
          />
        </SettingsSectionFooter>
      </form>
      <ConfirmationModal
        isOpen={confirming}
        onOpenChange={(open) => {
          if (!isLoading) setConfirming(open);
        }}
        title="Change credit calculation"
        confirmLabel="Create new plan"
        confirmVariant="primary"
        cancelVariant="ghost"
        isLoading={isLoading}
        onConfirm={() =>
          mutate({ creditCalculationModel: model, pagesPerCredit }, { onSuccess: () => setConfirming(false) })
        }
      >
        <div className="space-y-3 text-sm">
          <p>
            This is a significant billing change for <strong>{organisation.name}</strong>.
          </p>
          <p>
            Credit calculation:{' '}
            <strong>
              {organisation.creditCalculationModel === CreditCalculationModel.CostBased ? 'Cost-based' : 'Legacy'}
            </strong>{' '}
            → <strong>{model === CreditCalculationModel.CostBased ? 'Cost-based' : 'Legacy'}</strong>.
          </p>
          <p>
            Pages per credit: <strong>{organisation.pagesPerCredit}</strong> → <strong>{pagesPerCredit}</strong>.
          </p>
          <p>
            The current plan will close and a new plan will take effect immediately. Billing dates, credit allowances
            and seats will stay the same. Previous usage will not be recalculated.
          </p>
        </div>
      </ConfirmationModal>
    </SettingsSection>
  );
}
